import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GetSingleOrderDetailsObject } from "../../../../@types/DRO/PurchaseOrderLinesResponse";
import { GetPurchaseOrderListResponse } from "../../../../@types/DRO/PurchaseOrderResponse";
import { ProductOrderApi } from "../../../../api/ProductOrder/ProductOrderApi";
import { useLoading } from "../../../../views/Loader/LoadingContext";

interface TableProps {
  onEditOrder: (orderId: number) => void;
  currentPage: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  pageSize: number;
  setPageSize: React.Dispatch<React.SetStateAction<number>>;
  search: string;
}

const ApprovalListTableComponent: React.FC<TableProps> = ({
  onEditOrder,
  currentPage,
  setCurrentPage,
  pageSize,
  setPageSize,
  search,
}) => {
  const [data, setData] = useState<GetSingleOrderDetailsObject[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({});
  const { setLoading } = useLoading();
  const navigate = useNavigate();

  const columns = [
    { key: "#", label: "#", width: "w-16" },
    { key: "orderID", label: "Order ID", width: "w-24" },
    { key: "creation", label: "Creation Date", width: "w-40" },
    { key: "vendorName", label: "Vendor Name", width: "w-48" },
    { key: "status", label: "Status", width: "w-32" },
    { key: "approvedBy", label: "Approved by", width: "w-40" },
    { key: "amount", label: "Amount", width: "w-32" },
    { key: "actions", label: "Actions", width: "w-24" },
    // { key: "expand", label: "", width: "w-12" },
  ];

  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      try {
        const response: GetPurchaseOrderListResponse =
          await ProductOrderApi.api.getPurchaseOrderList(
            currentPage,
            pageSize,
            search,
            undefined,
            undefined,
            true
          );

        if (!response.data?.data) {
          throw new Error("Invalid response data");
        }

        const data = response.data.data;
        const transformedData = data.map((item) => ({
          id: item.id,
          status: item.status,
          confirmationDate: item.confirmationDate || "",
          vendorDetails: {
            vendorId: item.vendorDetails?.vendorId || 0,
            vendorName: item.vendorDetails?.vendorName || "N/A",
          },
          deliveryWareHouseDetails: {
            deliveryWarehouseId:
              item.deliveryWareHouseDetails?.deliveryWarehouseId || 0,
            deliveryWarehouseName:
              item.deliveryWareHouseDetails?.deliveryWarehouseName || "N/A",
            deliveryWarehouseCode:
              item.deliveryWareHouseDetails?.deliveryWarehouseCode || "N/A",
          },
          amount: item.amount || 0,
          totalAmount: null, // Default to null since list response doesn't include totalAmount
          purchaseOrderType: item.purchaseOrderType || "",
          gstTreatment: item.gstTreatment || "",
          expectedArrival: item.expectedArrival || "",
          updatedBy: null,
          notes: null,
          orderDate: "",
          purchaseOrderLines: null,
          approvals: item.approvals || [],
        }));

        setData(transformedData);
        setTotalPages(response.data.totalPages);
        setTotalItems(response.data.data.length);
      } catch (error) {
        console.error("Error fetching orders:", error);
        setData([]);
        setTotalPages(0);
        setTotalItems(0);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [currentPage, pageSize, search, setLoading]);

  const toggleRow = (id: number) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleViewOrder = (orderId: number) => {
    navigate(`/approval-request/view/${orderId}`);
  };

  const getStatusBadge = (
    status: string
  ): { className: string; label: string } => {
    const statusMap: Record<string, { className: string; label: string }> = {
      DRAFT: {
        className: "bg-gray-100 text-gray-800 border border-gray-300",
        label: "Draft",
      },
      CREATED: {
        className: "bg-blue-100 text-blue-800 border border-blue-300",
        label: "Created",
      },
      COMPLETED: {
        className: "bg-green-100 text-green-800 border border-green-300",
        label: "Completed",
      },
      APPROVED: {
        className: "bg-yellow-100 text-yellow-800 border border-yellow-300",
        label: "Approved",
      },
      REJECTED: {
        className: "bg-red-100 text-red-800 border border-red-300",
        label: "Rejected",
      },
    };
    return statusMap[status] || statusMap.DRAFT;
  };

  const getApprovedByNames = (approvals: any[], orderStatus: string) => {
    if (!approvals || approvals.length === 0) {
      return orderStatus === "DRAFT" ? "Not submitted" : "No approvers";
    }

    const approvedNames = approvals
      .filter((approval) => approval.status === "APPROVED")
      .map((approval) => approval.name);

    const pendingNames = approvals
      .filter((approval) => approval.status === "PENDING")
      .map((approval) => approval.name);

    if (approvedNames.length > 0) {
      return approvedNames.join(", ");
    } else if (pendingNames.length > 0) {
      return orderStatus === "DRAFT"
        ? "Not submitted"
        : `Pending (${pendingNames.join(", ")})`;
    }

    return "No approvers";
  };

  const getActionButton = (order: GetSingleOrderDetailsObject) => {
    if (order.status.name === "DRAFT") {
      return (
        <button
          onClick={() => onEditOrder(order.id)}
          className="px-3 py-1.5 bg-red-600 text-white rounded-md text-xs font-medium hover:bg-red-700 transition-colors">
          Edit
        </button>
      );
    } else {
      return (
        <button
          onClick={() => handleViewOrder(order.id)}
          className="px-3 py-1.5 bg-blue-600 text-white rounded-md text-xs font-medium hover:bg-blue-700 transition-colors">
          View
        </button>
      );
    }
  };

  return (
    <div className="w-full">
      {/* Table */}
      <div className="overflow-hidden">
        <table className="min-w-full bg-white">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={`${column.width} px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {data.length > 0 ? (
              data.map((row, index) => {
                const statusInfo = getStatusBadge(row.status.name);
                return (
                  <React.Fragment key={row.id}>
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-4 text-sm text-gray-900">
                        {currentPage * pageSize + index + 1}
                      </td>
                      <td className="px-4 py-4 text-sm font-medium text-gray-900">
                        #{row.id}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-900">
                        {row.confirmationDate}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-900">
                        {row.vendorDetails?.vendorName || "N/A"}
                      </td>
                      <td className="px-4 py-4">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusInfo.className}`}>
                          {statusInfo.label}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-900">
                        {getApprovedByNames(
                          row.approvals || [],
                          row.status.name
                        )}
                      </td>
                      <td className="px-4 py-4 text-sm font-medium text-gray-900">
                        {row.amount
                          ? `₹ ${row.amount.toLocaleString()}`
                          : "N/A"}
                      </td>
                      <td className="px-4 py-4">{getActionButton(row)}</td>
                    </tr>

                    {/* Expanded Row */}
                  </React.Fragment>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-4 py-12 text-center text-gray-500">
                  <div className="flex flex-col items-center">
                    <svg
                      className="w-12 h-12 text-gray-300 mb-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    <p className="text-lg font-medium text-gray-900 mb-1">
                      No approval requests found
                    </p>
                    <p className="text-gray-500">
                      Try adjusting your search criteria
                    </p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-white">
          <div className="flex items-center space-x-2">
            {/* First Page Button */}
            <button
              onClick={() => setCurrentPage(0)}
              disabled={currentPage === 0}
              className={`px-3 py-2 text-xs font-medium rounded-md border ${
                currentPage === 0
                  ? "border-gray-200 text-gray-400 cursor-not-allowed bg-gray-50"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50 bg-white"
              }`}>
              First
            </button>

            {/* Previous Button */}
            <button
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 0))}
              disabled={currentPage === 0}
              className={`p-2 rounded-md border ${
                currentPage === 0
                  ? "border-gray-200 text-gray-400 cursor-not-allowed"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}>
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>

            {/* Next Button */}
            <button
              onClick={() =>
                setCurrentPage((p) => Math.min(p + 1, totalPages - 1))
              }
              disabled={currentPage >= totalPages - 1}
              className={`p-2 rounded-md border ${
                currentPage >= totalPages - 1
                  ? "border-gray-200 text-gray-400 cursor-not-allowed"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}>
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>

            {/* Last Page Button */}
            <button
              onClick={() => setCurrentPage(totalPages - 1)}
              disabled={currentPage >= totalPages - 1}
              className={`px-3 py-2 text-xs font-medium rounded-md border ${
                currentPage >= totalPages - 1
                  ? "border-gray-200 text-gray-400 cursor-not-allowed bg-gray-50"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50 bg-white"
              }`}>
              Last
            </button>
          </div>

          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-700">
              Page {currentPage + 1} of {totalPages}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApprovalListTableComponent;
